/**
 * Analysis Pill Component
 *
 * A colored pill component for displaying analysis types with optional status icons.
 *
 * Features:
 * - Colored background based on analysis type (purple, blue, amber)
 * - Optional status icon embedded in the pill
 * - Size variants for different contexts (tables vs cards)
 * - Consistent styling across the application
 *
 * Usage:
 * ```tsx
 * // Basic usage
 * <AnalysisPill analysisType="Image Analysis" />
 *
 * // With status icon
 * <AnalysisPill analysisType="Gradation" status="completed" />
 *
 * // Small size for tables
 * <AnalysisPill analysisType="Wash Loss" status="pending" size="sm" />
 *
 * // Hide the status icon
 * <AnalysisPill analysisType="Image Analysis" status="completed" showStatusIcon={false} />
 * ```
 */

"use client";

import {
  getAnalysisTypeColorClasses,
  getAnalysisTypeConfig,
  getStatusIconConfig,
} from "@/lib/utils/analysis-utils";
import { cn } from "@/lib/utils/general-utils";

export type AnalysisPillSize = "xs" | "sm" | "md";

export interface AnalysisPillProps {
  /** The analysis type name (e.g., "Image Analysis", "Sieve Analysis") */
  readonly analysisType: string | null | undefined;
  /** Optional status to show icon (e.g., "completed", "pending") */
  readonly status?: string | null | undefined;
  /** Whether to show the status icon (default: true if status is provided) */
  readonly showStatusIcon?: boolean;
  /** Size variant */
  readonly size?: AnalysisPillSize;
  /** Additional CSS classes */
  readonly className?: string;
  /** Whether to show the analysis type icon (default: false) */
  readonly showTypeIcon?: boolean;
  /** Optional suffix text displayed after the label with a dot separator (e.g., "Run #3") */
  readonly suffix?: string;
}

const sizeClasses: Record<
  AnalysisPillSize,
  { pill: string; icon: string; typeIcon: string }
> = {
  xs: {
    pill: "px-1.5 py-0.5 text-[10px] gap-1",
    icon: "w-3 h-3",
    typeIcon: "w-3 h-3",
  },
  sm: {
    pill: "px-2.5 py-1 text-xs gap-1.5",
    icon: "w-3.5 h-3.5",
    typeIcon: "w-3.5 h-3.5",
  },
  md: {
    pill: "px-3 py-1.5 text-sm gap-2",
    icon: "w-4 h-4",
    typeIcon: "w-4 h-4",
  },
};

export function AnalysisPill({
  analysisType,
  status,
  showStatusIcon = true,
  size = "sm",
  className,
  showTypeIcon = false,
  suffix,
}: AnalysisPillProps) {
  const typeConfig = getAnalysisTypeConfig(analysisType);
  const colorClasses = getAnalysisTypeColorClasses(analysisType);
  const statusConfig = status ? getStatusIconConfig(status) : null;

  const TypeIcon = typeConfig.icon;
  const StatusIcon = statusConfig?.icon;

  const sizeConfig = sizeClasses[size];

  return (
    <span
      className={cn(
        "inline-flex items-center font-medium rounded-full border",
        colorClasses.bg,
        colorClasses.text,
        colorClasses.border,
        sizeConfig.pill,
        className,
      )}
    >
      {/* Analysis Type Icon (optional) */}
      {showTypeIcon && TypeIcon && (
        <TypeIcon className={cn(sizeConfig.typeIcon, "shrink-0")} />
      )}

      {/* Analysis Type Label */}
      <span className="truncate">{analysisType || "Unknown"}</span>

      {/* Optional suffix (e.g., "Run #3") */}
      {suffix && <span className="opacity-60 shrink-0">&middot; {suffix}</span>}

      {/* Status Icon — inherits pill text color via currentColor */}
      {showStatusIcon && StatusIcon && statusConfig && (
        <StatusIcon
          className={cn(
            sizeConfig.icon,
            statusConfig.animate && "animate-spin",
            "shrink-0",
          )}
        />
      )}
    </span>
  );
}

/**
 * Analysis Pills Group - Display multiple analysis types as pills
 *
 * Useful for showing multiple analyses in a row (e.g., in sample tables)
 */
export interface AnalysisPillsGroupProps {
  /** Array of analysis items with type and optional status */
  readonly analyses: ReadonlyArray<{
    readonly type: string | null | undefined;
    readonly status?: string | null | undefined;
    readonly suffix?: string;
  }>;
  /** Maximum number of pills to show before collapsing */
  readonly maxVisible?: number;
  /** Size variant for all pills */
  readonly size?: AnalysisPillSize;
  /** Whether to show status icons */
  readonly showStatusIcon?: boolean;
  /** Whether to show analysis type icons */
  readonly showTypeIcon?: boolean;
  /** Additional CSS classes for the container */
  readonly className?: string;
}

export function AnalysisPillsGroup({
  analyses,
  maxVisible = 3,
  size = "sm",
  showStatusIcon = true,
  showTypeIcon = false,
  className,
}: AnalysisPillsGroupProps) {
  const visibleAnalyses = analyses.slice(0, maxVisible);
  const hiddenCount = analyses.length - maxVisible;

  return (
    <div className={cn("flex flex-wrap items-center gap-1", className)}>
      {visibleAnalyses.map((analysis, index) => (
        <AnalysisPill
          key={`${analysis.type}-${index}`}
          analysisType={analysis.type}
          status={analysis.status}
          suffix={analysis.suffix}
          showStatusIcon={showStatusIcon}
          showTypeIcon={showTypeIcon}
          size={size}
        />
      ))}
      {hiddenCount > 0 && (
        <span className="text-xs text-text-tertiary font-medium px-1">
          +{hiddenCount}
        </span>
      )}
    </div>
  );
}
